import axios from '@/utils/axios';
import store from '@/utils/store';
import { deriveCartCount } from '@/utils/cart';

const handleError = async (error, router) => {
    console.error('Error:', error);

    const timelog_id = store.state.loggedUser?.timelog_id;

    // If string error
    if (typeof error === 'string' && error.toLowerCase().includes('unauthorized')) {
        await apiHelper.logout(router, timelog_id);
        router.push('/');
        return;
    }

    // If Axios error
    if (error.response) {
        const status = error.response.status;
        const errorMsg = error.response.data?.error?.toLowerCase() || '';
        const errorName = error.response.data?.name;

        const isTokenExpired =
            errorMsg.includes('token expired') ||
            errorMsg.includes('jwt expired') ||
            errorName === 'TokenExpiredError';

        const isInvalidToken =
            errorMsg.includes('invalid token') ||
            errorName === 'JsonWebTokenError';

        if (status === 403 || isTokenExpired || isInvalidToken) {
            await apiHelper.logout(router, timelog_id);
            // router.push('/');
            return;
        }
    }

    throw error;
};

const apiHelper = {
    async syncCartCount(router) {
        const cartId = store.state.loggedUser?.cart?.id;
        if (!cartId) {
            store.commit('setCartProducts', 0);
            return 0;
        }

        try {
            const response = await axios.get(`/cart/${cartId}`);
            const count = response.data?.success
                ? deriveCartCount(response.data.cart, 0)
                : 0;
            store.commit('setCartProducts', count);
            return count;
        } catch (error) {
            await handleError(error, router);
            return store.state.noOfCartProducts || 0;
        }
    },

    async logout(router, id) {
        try {
            const response = await axios.post('/logout', { data: id });
            if (response.data.success) {
                store.commit('setIsLoggedIn', false);
                store.commit('setLoggedUser', null);
                store.commit('setToggleAsideMenu', true);
                store.commit('setJWTToken', null);
                store.commit('setCartProducts', 0);
                store.commit('setToggleDarkMode', false);
                store.commit('setOrderDtl', { shipping_type: '' });
                router.push('/');
            } else {
                console.log(response.data);
            }
        } catch (error) {
            console.error('Error logging out:', error);
        }
    },

    async get(url, router) {
        try {
            const response = await axios.get(url);
            return response.data;
        } catch (error) {
            await handleError(error, router);
        }
    },

    async getInfoById(url, router) {
        try {
            const response = await axios.get(url, {
                headers: { // Manual adding Authorization header needer for success login
                    Authorization: `Bearer ${store.state.jwtToken}`,
                },
            });
            return response.data;
        } catch (error) {
            await handleError(error, router);
        }
    },

    async post(url, data, router) {
        try {
            const isFormData = data instanceof FormData;
            const response = await axios.post(url, isFormData ? data : { data }, {
                headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {},
            });
            return response.data;
        } catch (error) {
            await handleError(error, router);
        }
    },

    async put(url, data, router) {
        try {
            const response = await axios.put(url, { data });
            return response.data;
        } catch (error) {
            await handleError(error, router);
        }
    },

    async delete(url, router) {
        try {
            const response = await axios.delete(url);
            return response.data;
        } catch (error) {
            await handleError(error, router);
        }
    },

    async login(data, router) {
        try {
            const loginRes = await this.post('/login', data, router);
            if (loginRes?.success) {
                const userRes = await this.post('/user/logged', loginRes.token, router);
                if (userRes.success) {
                    localStorage.removeItem('chatRoomID');
                    store.commit('setJWTToken', loginRes.token);
                    store.commit('setIsLoggedIn', true);
                    store.commit('setLoggedUser', userRes.user);
                    store.commit('setToggleDarkMode', userRes.user.is_app_dark_mode);
                    store.commit('setToggleAsideMenu', false);
                    if (userRes.user.cart) {
                        store.commit('setCartProducts', deriveCartCount(userRes.user.cart, 0));
                    }
                    if (userRes.user.roles.includes(10)) {
                        store.commit('setToggleAsideMenu', true);
                    }
                }
            } else {
                throw new Error('Unsuccessful response');
            }
            return true;
        } catch (error) {
            await handleError(error, router);
        }
    }
};

export default apiHelper;
